import { useState } from 'react';
import { MapPin, Star, Trash2, Plus, Edit2, Copy } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AddressLine1Autocomplete } from './AddressLine1Autocomplete';
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/lib/supabase';
import { getCoordinates } from '@/lib/geocoding';
import { captureError } from '@/lib/sentry';

const ADDRESS_LABEL_OPTIONS = ['home', 'work', 'business', 'main', 'additional'] as const;

interface Address {
  id: string;
  contact_id: string;
  address_line1: string;
  address_line2?: string;
  city: string;
  state?: string;
  postal_code?: string;
  country: string;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
  label?: string;
  lat?: string;
  long?: string;
}

interface ContactAddressesProps {
  contactId: string;
  addresses: Address[];
  onUpdate: () => void;
}

export function ContactAddresses({ contactId, addresses, onUpdate }: ContactAddressesProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newAddress, setNewAddress] = useState<Partial<Address>>({});
  const [editedAddress, setEditedAddress] = useState<Partial<Address>>({});
  const { toast } = useToast();

  const addAddress = async () => {
    try {
      // Check if at least one field has a value
      const hasValue = Object.entries(newAddress).some(([key, value]) => {
        // Skip non-address fields
        if (!['address_line1', 'address_line2', 'city', 'state', 'postal_code', 'country'].includes(key)) {
          return false;
        }
        return value && typeof value === 'string' && value.trim() !== '';
      });

      if (!hasValue) {
        toast({
          title: "Validation Error",
          description: "Please fill in at least one address field",
          variant: "destructive",
        });
        return;
      }

      // Get coordinates if postal code is provided
      let coordinates = null;
      if (newAddress.postal_code) {
        coordinates = await getCoordinates(
          newAddress.postal_code,
          newAddress.country
        );
      }

      const { error } = await supabase.from('physical_addresses').insert({
        contact_id: contactId,
        ...newAddress,
        is_primary: addresses.length === 0,
        updated_at: new Date().toISOString(),
        ...(coordinates && {
          lat: coordinates.lat.toString(),
          long: coordinates.lon.toString()
        })
      });

      if (error) throw error;

      setNewAddress({});
      setIsAdding(false);
      onUpdate();
      
      toast({
        description: "Address added successfully",
      });
    } catch (error) {
      console.error('Error adding address:', error);
      captureError(error as Error, { feature: 'contact-addresses-add' });
      toast({
        title: "Error",
        description: "Failed to add address",
        variant: "destructive",
      });
    }
  };

  const deleteAddress = async (id: string) => {
    try {
      const { error } = await supabase
        .from('physical_addresses')
        .delete()
        .eq('id', id);

      if (error) throw error;

      onUpdate();
      
      toast({
        description: "Address removed successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-addresses-delete-address' });
      toast({
        title: "Error",
        description: "Failed to remove address",
        variant: "destructive",
      });
    }
  };

  const setPrimary = async (id: string) => {
    try {
      // First, set all addresses to non-primary
      const { error: resetError } = await supabase
        .from('physical_addresses')
        .update({ is_primary: false })
        .eq('contact_id', contactId);

      if (resetError) throw resetError;

      // Then set the selected address as primary
      const { error } = await supabase
        .from('physical_addresses')
        .update({ 
          is_primary: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;

      onUpdate();
      
      toast({
        description: "Primary address updated successfully",
      });
    } catch (error) {
      captureError(error as Error, { feature: 'contacts-contact-addresses-set-primary' });
      toast({
        title: "Error",
        description: "Failed to update primary address",
        variant: "destructive",
      });
    }
  };

  // Initialize the edited address state when editing begins
  const startEditing = (address: Address) => {
    setEditingId(address.id);
    setEditedAddress({
      address_line1: address.address_line1 || '',
      address_line2: address.address_line2 || '',
      city: address.city || '',
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country || '',
      label: address.label || 'home',
    });
  };

  const updateAddress = async (id: string) => {
    try {
      const originalAddress = addresses.find(a => a.id === id);
      if (!originalAddress) {
        return;
      }

      // Check if at least one field has a value
      const hasValue = Object.entries(editedAddress).some(([key, value]) => {
        // Skip non-address fields
        if (!['address_line1', 'address_line2', 'city', 'state', 'postal_code', 'country'].includes(key)) {
          return false;
        }
        return value && typeof value === 'string' && value.trim() !== '';
      });

      if (!hasValue) {
        toast({
          title: "Validation Error",
          description: "Please fill in at least one address field",
          variant: "destructive",
        });
        return;
      }

      // Check if postal code has changed
      const postalCodeChanged = editedAddress.postal_code && 
                            editedAddress.postal_code !== originalAddress.postal_code;

      // Get coordinates if postal code changed
      let coordinates = null;
      if (postalCodeChanged) {
        coordinates = await getCoordinates(
          editedAddress.postal_code!,
          editedAddress.country || originalAddress.country
        );
      }

      const updateData = {
        ...editedAddress,
        updated_at: new Date().toISOString(),
        ...(coordinates && {
          lat: coordinates.lat.toString(),
          long: coordinates.lon.toString()
        })
      };

      const { error } = await supabase
        .from('physical_addresses')
        .update(updateData)
        .eq('id', id)
        .select();

      if (error) {
        throw error;
      }

      setEditingId(null);
      setEditedAddress({});
      onUpdate();
      
      toast({
        description: "Address updated successfully",
      });
    } catch (error) {
      console.error('Error updating address:', error);
      captureError(error as Error, { feature: 'contact-addresses-update' });
      toast({
        title: "Error",
        description: "Failed to update address",
        variant: "destructive",
      });
    }
  };

  const handleAddressInputChange = (field: keyof Address, value: string) => {
    setEditedAddress(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatAddress = (address: Address): string => {
    const parts = [
      address.address_line1,
      address.address_line2,
      [address.city, address.state, address.postal_code].filter(Boolean).join(', '),
      address.country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const getGoogleMapsUrl = (address: Address): string => {
    if (address.lat && address.long) {
      return `https://www.google.com/maps?q=${address.lat},${address.long}`;
    }
    const query = encodeURIComponent(formatAddress(address));
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  return (
    <div className="space-y-2">
      <h2 className="font-semibold text-sm">Addresses</h2>
      
      {addresses.map((address) => (
        <div
          key={address.id}
          className="group flex items-center justify-between p-2 rounded-md hover:bg-muted/50 transition-colors"
        >
          <div className="flex gap-2">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-1" />
            <div>
              {editingId === address.id ? (
                <div className="space-y-2">
                  <AddressLine1Autocomplete
                    value={editedAddress.address_line1 || ''}
                    onChange={(val) => handleAddressInputChange('address_line1', val)}
                    onSuggestionSelect={(addr) => {
                      handleAddressInputChange('address_line1', addr.address_line1);
                      handleAddressInputChange('city', addr.city);
                      handleAddressInputChange('state', addr.state);
                      handleAddressInputChange('postal_code', addr.postal_code);
                      handleAddressInputChange('country', addr.country);
                      if (addr.lat) setEditedAddress(prev => ({ ...prev, lat: addr.lat! }));
                      if (addr.long) setEditedAddress(prev => ({ ...prev, long: addr.long! }));
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); updateAddress(address.id); } else if (e.key === 'Escape') setEditingId(null); }}
                    placeholder="Address Line 1"
                    className="h-7 text-xs"
                  />
                  <Input
                    value={editedAddress.address_line2 || ''}
                    onChange={(e) => handleAddressInputChange('address_line2', e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); updateAddress(address.id); } else if (e.key === 'Escape') setEditingId(null); }}
                    placeholder="Address Line 2 (Optional)"
                    className="h-7 text-xs"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={editedAddress.city || ''}
                      onChange={(e) => handleAddressInputChange('city', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); updateAddress(address.id); } else if (e.key === 'Escape') setEditingId(null); }}
                      placeholder="City"
                      className="h-7 text-xs"
                    />
                    <Input
                      value={editedAddress.state || ''}
                      onChange={(e) => handleAddressInputChange('state', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); updateAddress(address.id); } else if (e.key === 'Escape') setEditingId(null); }}
                      placeholder="State/Province"
                      className="h-7 text-xs"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={editedAddress.postal_code || ''}
                      onChange={(e) => handleAddressInputChange('postal_code', e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); updateAddress(address.id); } else if (e.key === 'Escape') setEditingId(null); }}
                      placeholder="Postal Code"
                      className="h-7 text-xs"
                    />
                    <Input
                      value={editedAddress.country || ''}
                      onChange={(e) => handleAddressInputChange('country', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          updateAddress(address.id);
                        } else if (e.key === 'Escape') {
                          setEditingId(null);
                        }
                      }}
                      placeholder="Country"
                      className="h-7 text-xs"
                    />
                  </div>
                  <Select
                    value={editedAddress.label || 'home'}
                    onValueChange={(value) => handleAddressInputChange('label', value)}
                  >
                    <SelectTrigger className="h-7 text-xs w-[130px]">
                      <SelectValue placeholder="Label" />
                    </SelectTrigger>
                    <SelectContent>
                      {ADDRESS_LABEL_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt} className="text-xs capitalize">{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={() => updateAddress(address.id)}
                      size="sm"
                      className="h-7 text-xs"
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setEditingId(null)}
                      size="sm"
                      className="h-7 text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <a
                      href={getGoogleMapsUrl(address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs hover:underline"
                    >
                      {address.address_line1}
                    </a>
                    {address.is_primary && (
                      <Star className="h-3.5 w-3.5 fill-current text-yellow-500" />
                    )}
                    {address.label && address.label !== 'additional' && (
                      <Badge variant="secondary" className="text-xs capitalize">{address.label}</Badge>
                    )}
                  </div>
                  {address.address_line2 && (
                    <div className="text-xs text-muted-foreground">
                      {address.address_line2}
                    </div>
                  )}
                  <div className="text-xs text-muted-foreground">
                    {[address.city, address.state, address.postal_code].filter(Boolean).join(', ')}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {address.country}
                  </div>
                </>
              )}
            </div>
          </div>
          {!editingId && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => startEditing(address)}
                className="h-6 w-6"
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigator.clipboard.writeText(formatAddress(address))}
                className="h-6 w-6"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
              {!address.is_primary && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setPrimary(address.id)}
                  className="h-6 w-6"
                >
                  <Star className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => deleteAddress(address.id)}
                className="h-6 w-6 text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
      ))}

      {isAdding ? (
        <div className="space-y-2 p-2 border rounded-lg">
          <div className="grid gap-2">
            <AddressLine1Autocomplete
              value={newAddress.address_line1 || ''}
              onChange={(val) => setNewAddress(prev => ({ ...prev, address_line1: val }))}
              onSuggestionSelect={(addr) => {
                setNewAddress(prev => ({
                  ...prev,
                  address_line1: addr.address_line1,
                  city: addr.city,
                  state: addr.state,
                  postal_code: addr.postal_code,
                  country: addr.country,
                  ...(addr.lat ? { lat: addr.lat } : {}),
                  ...(addr.long ? { long: addr.long } : {}),
                }));
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAddress(); } else if (e.key === 'Escape') { setIsAdding(false); setNewAddress({}); } }}
              placeholder="Address Line 1"
              className="h-7 text-xs"
            />
            <Input
              placeholder="Address Line 2 (Optional)"
              value={newAddress.address_line2 || ''}
              onChange={(e) => setNewAddress(prev => ({ ...prev, address_line2: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAddress(); } else if (e.key === 'Escape') { setIsAdding(false); setNewAddress({}); } }}
              className="h-7 text-xs"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="City"
                value={newAddress.city || ''}
                onChange={(e) => setNewAddress(prev => ({ ...prev, city: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAddress(); } else if (e.key === 'Escape') { setIsAdding(false); setNewAddress({}); } }}
                className="h-7 text-xs"
              />
              <Input
                placeholder="State/Province"
                value={newAddress.state || ''}
                onChange={(e) => setNewAddress(prev => ({ ...prev, state: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAddress(); } else if (e.key === 'Escape') { setIsAdding(false); setNewAddress({}); } }}
                className="h-7 text-xs"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Postal Code"
                value={newAddress.postal_code || ''}
                onChange={(e) => setNewAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAddress(); } else if (e.key === 'Escape') { setIsAdding(false); setNewAddress({}); } }}
                className="h-7 text-xs"
              />
              <Input
                placeholder="Country"
                value={newAddress.country || ''}
                onChange={(e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addAddress();
                  } else if (e.key === 'Escape') {
                    setIsAdding(false);
                    setNewAddress({});
                  }
                }}
                className="h-7 text-xs"
              />
            </div>
            <Select
              value={newAddress.label || 'home'}
              onValueChange={(value) => setNewAddress(prev => ({ ...prev, label: value }))}
            >
              <SelectTrigger className="h-7 text-xs w-[130px]">
                <SelectValue placeholder="Label" />
              </SelectTrigger>
              <SelectContent>
                {ADDRESS_LABEL_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt} className="text-xs capitalize">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button 
              onClick={addAddress}
              size="sm"
              className="h-7 text-xs"
            >
              Add
            </Button>
            <Button 
              variant="ghost"
              onClick={() => {
                setIsAdding(false);
                setNewAddress({});
              }}
              size="sm"
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground text-xs h-7"
          onClick={() => setIsAdding(true)}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Address
        </Button>
      )}
    </div>
  );
}